import React from "react";
import MuiDialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";

import type { ModalAnalyticsId } from "@/analytics/modalIds";
import { useModalAnalytics } from "@/analytics/useModalAnalytics";

export interface DialogProps {
  /** Names this dialog in analytics. Required so a new one cannot ship
   * untracked — see `src/analytics/modalIds.ts` for why it can't be derived
   * from the title. */
  analyticsId: ModalAnalyticsId;
  open: boolean;
  title: string;
  /** A plain string renders as body copy; pass a node to compose in extra
   * content (e.g. a type-to-confirm input) between the message and the
   * footer actions. */
  message: React.ReactNode;
  onConfirm: () => void;
  onCancel: () => void;
  confirmLabel: string;
  cancelLabel: string;
  /** Red confirm action, for irreversible/destructive choices. */
  destructive?: boolean;
  /** Gate the confirm action on state the caller owns (e.g. a matching
   * type-to-confirm input). */
  confirmDisabled?: boolean;
  /** MUI's default: a backdrop click cancels, same as Escape. Set false to
   * require an explicit button press. */
  closeOnBackdropClick?: boolean;
}

/**
 * The Confirm Dialog pattern from the design system: title, message, and
 * exactly two footer actions — no close (X). Cancel/Escape/backdrop all
 * route to `onCancel`; the confirm action is the only way out that isn't a
 * cancel. Visual treatment (radius, shadow, inset footer) comes from the
 * `MuiDialog`/`MuiDialogActions` theme overrides, not from styles here.
 */
const Dialog: React.FC<DialogProps> = ({
  analyticsId,
  open,
  title,
  message,
  onConfirm,
  onCancel,
  confirmLabel,
  cancelLabel,
  destructive = false,
  confirmDisabled = false,
  closeOnBackdropClick = true,
}) => {
  const titleId = React.useId();
  const { reportClose } = useModalAnalytics({ analyticsId, surface: "dialog", open });

  // Reported before the caller's handler runs, because that handler is usually
  // what flips `open` — by the time it returns, the close event has been built.
  const handleConfirm = () => {
    reportClose("confirmed", "confirm_button");
    onConfirm();
  };

  const handleCancel = () => {
    reportClose("cancelled", "cancel_button");
    onCancel();
  };

  const handleClose = (
    _event: unknown,
    reason?: "backdropClick" | "escapeKeyDown",
  ) => {
    if (reason === "backdropClick" && !closeOnBackdropClick) return;
    // Both land on onCancel, so the outcome is the same either way; `via` is
    // what separates a considered Cancel from a dismissal.
    reportClose("cancelled", reason === "backdropClick" ? "backdrop" : "escape");
    onCancel();
  };

  return (
    <MuiDialog open={open} onClose={handleClose} aria-labelledby={titleId}>
      <DialogTitle id={titleId}>{title}</DialogTitle>
      <DialogContent>
        {typeof message === "string" ? (
          <DialogContentText>{message}</DialogContentText>
        ) : (
          message
        )}
      </DialogContent>
      <DialogActions>
        <Button variant="text" onClick={handleCancel}>
          {cancelLabel}
        </Button>
        <Button
          variant="contained"
          color={destructive ? "error" : "primary"}
          onClick={handleConfirm}
          disabled={confirmDisabled}
        >
          {confirmLabel}
        </Button>
      </DialogActions>
    </MuiDialog>
  );
};

export default Dialog;
